import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Printer } from "lucide-react";
import { http } from "@/lib/api";

/** Tiny live badge — sits in the sidebar footer so the owner sees print
 * pipeline health from every screen without opening Admin.
 *
 * Polls `/admin/print-health` every 30s. Color rules:
 *   • emerald → success_rate ≥ 95% AND no stuck job AND no pending
 *   • amber   → pending > 0 OR success_rate 80-95%
 *   • red     → stuck_job present OR success_rate < 80%
 *
 * Click → /admin (the full dashboard panel).
 */
export default function PrintHealthBadge({ collapsed = false }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await http.get("/admin/print-health");
      setData(r.data);
    } catch {
      // Silently drop — badge just doesn't render if the user lacks perms
      // or the endpoint is briefly down.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  if (!data) return null;

  const stuck = !!data.stuck_job;
  const pending = (data.status_counts?.pending || 0) + (data.status_counts?.claimed || 0);
  const successPct = Number(data.throughput_24h?.success_rate ?? 100);

  let tone = "emerald";
  let label = "OK";
  if (stuck || successPct < 80) {
    tone = "red";
    label = stuck ? "Κολλημένο job" : "Σφάλματα";
  } else if (pending > 0 || successPct < 95) {
    tone = "amber";
    label = pending > 0 ? `${pending} σε αναμονή` : `${successPct.toFixed(0)}%`;
  } else {
    label = `${data.throughput_24h?.printed || 0} σήμερα`;
  }

  const palette = {
    emerald: "bg-emerald-50 border-emerald-300 text-emerald-700 hover:bg-emerald-100",
    amber:   "bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100",
    red:     "bg-red-50 border-red-300 text-red-700 hover:bg-red-100 animate-pulse",
  }[tone];

  if (collapsed) {
    return (
      <Link
        to="/admin"
        title={`Εκτυπώσεις · ${label}`}
        className={`grid place-items-center w-8 h-8 mx-auto rounded-md border ${palette} transition-colors`}
        data-testid="print-health-badge-collapsed"
      >
        <Printer size={14} />
      </Link>
    );
  }

  return (
    <Link
      to="/admin"
      className={`flex items-center gap-1.5 px-2 py-1.5 rounded-md border text-[11px] font-bold transition-colors ${palette}`}
      data-testid="print-health-badge"
      title="Άνοιγμα Print Health Dashboard"
    >
      <Printer size={12} />
      <span className="truncate">{label}</span>
    </Link>
  );
}
